import Card from '@/components/Card';
import { DescriptionComponent, MainHeader, StatusChip } from '../styles';
import Typography from '@mui/material/Typography';
import DescriptionPopper from '@/components/DescriptionPopper';
import { EditButton } from '../../General/styles';
import { Pencil, WifiPen } from 'lucide-react';
import { BadgeTitle, BadgeValue, CardBody, InfoBadge } from '../../styles';
import Skeleton from '@mui/material/Skeleton';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { wifiFields } from '@/utils/constants';

interface AccessPointInterface {
  currentFrequency: '2.4GHz' | '5GHz';
  loading: boolean;
  currentAp: number;
  modeView: string;
  wifis: WifiListType;
  setCurrentModal: Dispatch<
    SetStateAction<
      | 'radio24'
      | 'radio50'
      | 'wifi24'
      | 'wifi50'
      | 'apSelect24'
      | 'apSelect50'
      | null
    >
  >;
}

function AccessPoint({
  currentFrequency,
  loading,
  currentAp,
  wifis,
  modeView,
  setCurrentModal,
}: AccessPointInterface) {
  const { t } = useTranslation();
  const activeAp =
    wifis[currentFrequency]?.[currentAp]?.ssidEnable?.value === 'true';
  const visibleAp =
    wifis[currentFrequency]?.[currentAp]?.visible?.value === 'true';
  const apNull =
    wifis[currentFrequency]?.[currentAp]?.ssidEnable == null ||
    wifis[currentFrequency]?.[currentAp]?.visible == null;

  return (
    <Card sx={{ maxWidth: '100%' }}>
      <MainHeader>
        <div className="divHeader">
          <Typography variant="h3">
            {t('equipment.wifis.accessPoint')} {currentAp + 1}
          </Typography>
          <StatusChip
            status={
              apNull ? 'unknown' : activeAp && visibleAp ? 'online' : 'offline'
            }
            label={
              apNull
                ? t('common.unknown')
                : activeAp && visibleAp
                  ? t('common.active')
                  : t('common.inactive')
            }
          />
        </div>

        <div className="divHeader">
          <DescriptionPopper
            description={
              <DescriptionComponent>
                {t('equipment.radio.selectAp')}
              </DescriptionComponent>
            }
            placement="top"
            children={
              <EditButton
                onClick={() =>
                  setCurrentModal(
                    currentFrequency === '2.4GHz' ? 'apSelect24' : 'apSelect50'
                  )
                }
              >
                <WifiPen size={18} />
              </EditButton>
            }
          />
          <DescriptionPopper
            description={
              <DescriptionComponent>
                {t('equipment.radio.editAp')}
              </DescriptionComponent>
            }
            placement="top"
            children={
              <EditButton
                onClick={() =>
                  setCurrentModal(
                    currentFrequency === '2.4GHz' ? 'wifi24' : 'wifi50'
                  )
                }
              >
                <Pencil size={18} />
              </EditButton>
            }
          />
        </div>
      </MainHeader>

      <CardBody modeView={modeView === 'grid'}>
        {wifiFields.map((field: EditorPropType) => (
          <InfoBadge modeView={modeView}>
            <BadgeTitle modeView={modeView}>
              {t(`equipment.wifis.${field.field}`)}
            </BadgeTitle>
            {!loading ? (
              <BadgeValue modeView={modeView}>
                {wifis[currentFrequency] &&
                  wifis[currentFrequency][currentAp]?.[field.field]?.value}
              </BadgeValue>
            ) : (
              <Skeleton
                variant="rounded"
                width={modeView === 'grid' ? '225px' : '100%'}
                height={14}
              />
            )}
          </InfoBadge>
        ))}
      </CardBody>
    </Card>
  );
}

export default AccessPoint;
